import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { turfMapApi } from '../lib/api'
import type {
  AutoGenerateTurfMapRequest,
  BatchCutTurfsRequest,
  UpdateTurfMapRequest,
  TurfMapFilters,
} from '../types/turf'

export function useTurfMaps(filters?: TurfMapFilters) {
  return useQuery({
    queryKey: ['turf-maps', filters],
    queryFn: async () => {
      const response = await turfMapApi.getAll(filters)
      return response.data
    },
  })
}

export function useTurfMapDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['turf-maps', id],
    queryFn: async () => {
      if (!id) throw new Error('Turf map ID is required')
      const response = await turfMapApi.getById(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useBatchCutTurfs() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: BatchCutTurfsRequest) =>
      turfMapApi.batchCut(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turf-maps'] })
      queryClient.invalidateQueries({ queryKey: ['turfs'] })
      // Invalidate campaign queries so hasTurfMap flag updates immediately
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
}

export function useUpdateTurfMap(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateTurfMapRequest) =>
      turfMapApi.update(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['turf-maps', id], data)
      queryClient.invalidateQueries({ queryKey: ['turf-maps'] })
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'campaigns' &&
          query.queryKey.includes('picker') &&
          query.queryKey.includes('turfmaps'),
      })
    },
  })
}

export function useDeleteTurfMap() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => turfMapApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turf-maps'] })
      queryClient.invalidateQueries({ queryKey: ['turfs'] })
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'campaigns' &&
          query.queryKey.includes('picker') &&
          query.queryKey.includes('turfmaps'),
      })
    },
  })
}

export function useAutoGenerateTurfMap() {
  // { jobId } — actual completion is polled (WI-41). The mutation only resolves once the
  // enqueue (202) is accepted, before the job has run — so cache invalidation belongs at the
  // poll's terminal-success point (see AutoGenerateModal/CampaignAutoGenerateModal), not here.
  return useMutation({
    mutationFn: (data: AutoGenerateTurfMapRequest) =>
      turfMapApi.autoGenerate(data).then(r => r.data),
  })
}

export function useMergeList() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ turfMapId, contactListId }: { turfMapId: string; contactListId: string }) =>
      turfMapApi.mergeList(turfMapId, contactListId).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['turf-maps'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
}

export function useAutoGeneratePreview() {
  // { jobId } — actual completion is polled (WI-41). The preview doesn't persist anything
  // server-side, so there is no cache to invalidate at the poll's terminal-success point
  // (see TurfCutterPage.handleAutoTurf) — the result just loads into local drawing-map state.
  return useMutation({
    mutationFn: (data: AutoGenerateTurfMapRequest) =>
      turfMapApi.autoGeneratePreview(data).then(r => r.data),
  })
}

export function useTurfMapHouseholds(id: string | undefined, fromSourceList?: boolean) {
  return useQuery({
    queryKey: ['turf-maps', id, 'households', { fromSourceList }],
    queryFn: async () => {
      if (!id) throw new Error('Turf map ID is required')
      const response = await turfMapApi.getHouseholds(id, fromSourceList)
      return response.data
    },
    enabled: !!id,
  })
}

export function useTurfMapContactsWithEmployment(turfMapId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: ['turf-maps', turfMapId, 'contacts-with-employment'],
    queryFn: async () => {
      if (!turfMapId) throw new Error('TurfMap ID is required')
      const response = await turfMapApi.getContactsWithEmployment(turfMapId)
      return response.data
    },
    enabled: enabled && !!turfMapId,
    staleTime: 5 * 60 * 1000,
  })
}
